import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
import GoogleTranslateWidget from '../../../../components/GoogleTranslateWidget';
export function BlogPostNarcissismGaslightingKO() {
  const articleData = {
    title: '용어에 대해 알아보기: 나르시시즘과 가스라이팅',
    description: '나르시시즘이나 가스라이팅 행동에 혼란스러우신가요? 텍스트의 주요 징후를 배우고 Toxic Detector의 AI가 미묘한 조작 패턴을 즉시 식별하는 방법을 확인하세요.',
    slug: 'narcissism-gaslighting-explained',
    publishDate: '2025-07-15',
    author: 'Toxic Detector Editorial'
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('링크가 클립보드에 복사되었습니다!');
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-8305">
      <GoogleTranslateWidget data-id="element-8306" />
      <Helmet data-id="element-8307">
        <title data-id="element-8308">나르시시즘과 가스라이팅 설명 | ToxicDetector</title>
        <meta name="description" content="나르시시즘과 가스라이팅 조작 전략 뒤에 있는 심리학을 이해합니다." data-id="element-8309" />
        <meta name="keywords" content="나르시시즘, 가스라이팅, 조작, 심리학, AI 분석" data-id="element-8310" />
        <meta property="og:image" content="https://cdn.magicpatterns.com/uploads/iLMUMmEFFWiRSqv8DSTAdz/file_00000000f0c8720a9c4e7f9e2fbdea6d.png" data-id="element-8311" />
        <link rel="canonical" href={`https://toxicdetector.com/ko/blog/${articleData.slug}`} data-id="element-8312" />
        <script type="application/ld+json" data-id="element-8313">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: '나르시시즘과 가스라이팅 설명',
          description: '나르시시즘이나 가스라이팅 행동에 혼란스러우신가요? 텍스트의 주요 징후를 배우고 Toxic Detector의 AI가 미묘한 조작 패턴을 즉시 식별하는 방법을 확인하세요.',
          image: 'https://toxicdetector.com/ko/blog/narcissism-gaslighting-explained.jpg',
          author: {
            '@type': 'Person',
            name: 'Toxic Detector Editorial'
          },
          publisher: {
            '@type': 'Organization',
            name: 'ToxicDetector',
            logo: {
              '@type': 'ImageObject',
              url: 'https://toxicdetector.com/logo.png'
            }
          },
          datePublished: '2025-07-15',
          dateModified: '2025-07-15'
        })}
        </script>
      </Helmet>

      <header className="bg-white/90 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm" data-id="element-8314">
        <div className="max-w-3xl mx-auto px-6 py-4" data-id="element-8315">
          <div className="flex items-center justify-between" data-id="element-8316">
            <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900" data-id="element-8317">
              🚩 ToxicDetector
            </Link>
            <button onClick={handleShare} className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors" aria-label="Share article" data-id="element-8318">
              <Share2 size={20} className="text-gray-600" data-id="element-8319" />
            </button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-8320">
        <Link to="/blog" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-8321">
          <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-8322" />
          블로그로 돌아가기
        </Link>

        <header className="mb-12" data-id="element-8323">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight text-gray-900" data-id="element-8324">
            {articleData.title}
          </h1>
          <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-8325">
            <time dateTime={articleData.publishDate} data-id="element-8326">
              {new Date(articleData.publishDate).toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </time>
            <span className="text-gray-300" data-id="element-8327">•</span>
            <span data-id="element-8328">{articleData.author}</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none" data-id="element-8329">
          <p className="text-xl mb-8 leading-relaxed text-gray-700" data-id="element-8330">
            왜 일부 사람들이 관계에서 당신을 혼란스럽게 하고, 지치게 하고,
            자신을 의심하게 만드는지 궁금한 적이 있나요? 그것은 종종{' '}
            <strong data-id="element-8331">나르시시즘과 가스라이팅</strong>이 작용하고 있습니다.
          </p>

          <section className="mb-12" data-id="element-8332">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-8333">
              나르시시즘이란 무엇인가요?
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-8334">
              <strong data-id="element-8335">나르시시즘</strong>은 자기중심적이고 조작적인 행동
              패턴으로, 다른 사람보다 자신의 필요를 우선시합니다.
            </p>
          </section>

          <section className="mb-12" data-id="element-8336">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-8337">
              가스라이팅이란 무엇인가요?
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-8338">
              <strong data-id="element-8339">가스라이팅</strong>은 당신 자신의 현실, 기억 또는 감정을
              의심하게 만드는 심리적 조작 기술입니다.
            </p>
          </section>

          <section className="mb-12" data-id="element-8340">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-8341">
              AI가 이러한 패턴을 감지하는 방법
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-8342">
              추측하는 대신, 우리의 AI는 조작 패턴을 위해 대화를 분석합니다.
            </p>
          </section>

          <section className="mb-12" data-id="element-8343">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-8344">
              이것을 이해하는 것이 중요한 이유
            </h2>
            <p className="text-lg leading-relaxed text-gray-600" data-id="element-8345">
              <strong data-id="element-8346">나르시시즘</strong> 또는 <strong data-id="element-8347">가스라이팅</strong>을
              조기에 인식하면 정서적 건강을 보호합니다.
            </p>
          </section>
        </div>

        <div className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-8348">
          <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-8349">
            💀 진실을 볼 준비가 되셨나요?
          </h2>
          <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-8350">
            추측을 멈추세요. Toxic Detector에 텍스트를 업로드하고 즉시 독성
            점수를 받으세요.
          </p>
          <Link to="/signup" className="inline-block bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors" data-id="element-8351">
            지금 채팅 스캔하기 →
          </Link>
        </div>

        <div className="mt-16" data-id="element-8352">
          <RelatedBlogPosts posts={[{
          title: '가스라이팅의 7가지 징후',
          description: 'AI가 텍스트 대화에서 감지하는 주요 가스라이팅 패턴을 배웁니다.',
          slug: '7-signs-of-gaslighting',
          emoji: '💀'
        }, {
          title: '당신의 전 애인은 은밀한 나르시시스트인가요?',
          description: '텍스트 분석을 통해 전 애인이 은밀한 나르시시즘 행동의 징후를 보이는지 확인하세요.',
          slug: 'is-your-ex-a-covert-narcissist',
          emoji: '🎭'
        }]} data-id="element-8353" />
        </div>
      </article>

      <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-8354">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-8355">
          <p className="text-gray-500 text-sm" data-id="element-8356">
            © {new Date().getFullYear()} ToxicDetector. 모든 권리 보유.
          </p>
        </div>
      </footer>
    </div>;
}